'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';

import { RootState } from '@/redux/store';
import {
  useGetUnreadMessagesCountQuery,
  useGetNotificationCountQuery
} from '@/redux/api/notificationsApi';
import { useGetUserQuery } from '@/redux/api/userApi';
import { COLORS } from '@/constants/colors';
import { getAuthStatus } from '@/utils/cookieManager';
import { classes } from './styles';
import NotificationPanel from './NotificationPanel';
import userContained from '@public/assets/svg/user-outlined.svg';

// --- Types ---

interface NavUserActionsProps {
  isCritical?: boolean;
}

// --- Components ---

const UserProfileSkeleton = () => (
  <Stack direction="row" ml={{ xs: 1, sm: 2 }} alignItems="center">
    <Box sx={classes.iconBox}>
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Box sx={classes.iconBox}>
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }}>
      <Skeleton
        variant="circular"
        sx={{ width: { lg: 40, xl: 48 }, height: { lg: 40, xl: 48 } }}
      />
      <Stack ml={1.5} sx={{ display: { xs: 'none', xl: 'flex' } }}>
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={180} height={16} />
      </Stack>
    </Stack>
  </Stack>
);

// --- Main Component ---

const NavUserActions = ({ isCritical = true }: NavUserActionsProps) => {
  const [openNotificationMenu, setOpenNotificationMenu] =
    useState<null | HTMLElement>(null);
  const pathname = usePathname();
  const authUser = useSelector((state: RootState) => state.auth?.user);

  const [mounted, setMounted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // --- Auth Verification Logic ---

  const verifyAuth = useCallback(() => {
    const status = getAuthStatus();
    const loggedIn =
      (status.hasToken && !status.isTokenExpired) ||
      (status.hasRefreshToken && !status.isRefreshTokenExpired);
    setIsLoggedIn(loggedIn);
    return status;
  }, []);

  useEffect(() => {
    setMounted(true);
    verifyAuth();

    // Re-verify on focus/visibility change to handle tab-idle expiry
    const handleReverify = () => verifyAuth();
    window.addEventListener('focus', handleReverify);
    window.addEventListener('visibilitychange', handleReverify);
    // Re-verify when any part of the app explicitly clears auth (e.g. session expiry)
    window.addEventListener('authLogout', handleReverify);

    // Set a timer to flip the state when the session is expected to fully expire
    let expiryTimer: NodeJS.Timeout;

    const getExpiry = (tokenName: string) => {
      try {
        const t = Cookies.get(tokenName);
        if (t && t.includes('.')) {
          const parts = t.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            return payload.exp ? payload.exp * 1000 - Date.now() : -1;
          }
        }
      } catch {
        // ignore
      }
      return -1;
    };

    if (isLoggedIn) {
      const accessExpiry = getExpiry('access_token');
      const refreshExpiry = getExpiry('refresh_token');

      // We want to flip the state to logged out only when ALL valid tokens expire.
      // So we wait for the longest valid expiry.
      const maxExpiry = Math.max(accessExpiry, refreshExpiry);

      if (maxExpiry > 0) {
        // Add a small buffer (1s) to ensure the server also sees it as expired
        expiryTimer = setTimeout(() => {
          verifyAuth();
        }, maxExpiry + 1000);
      }
    }

    return () => {
      window.removeEventListener('focus', handleReverify);
      window.removeEventListener('visibilitychange', handleReverify);
      window.removeEventListener('authLogout', handleReverify);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [verifyAuth, pathname, isLoggedIn]);

  // --- API Queries ---

  /*
  const { data: notificationsData, isLoading: isLoadingNotifications } =
    useGetNotificationsQuery(
      { read_status: 'any', isCritical },
      { skip: !isLoggedIn }
    );
  */

  const { data: unreadMessagesCount } = useGetUnreadMessagesCountQuery(
    undefined,
    { skip: !isLoggedIn }
  );

  const { data: notificationCountData } = useGetNotificationCountQuery(
    undefined,
    { skip: !isLoggedIn }
  );

  const { data: userData, currentData: currentUserData } = useGetUserQuery(
    { isCritical },
    {
      skip: !isLoggedIn,
      refetchOnMountOrArgChange: false
    }
  );

  // --- Derived State ---

  const unreadConversationsCount = unreadMessagesCount?.count ?? 0;
  const unreadCount = notificationCountData?.count ?? 0;
  // const notificationsLoader = isLoadingNotifications && !notificationsData;

  const authUserForDisplay = useMemo(() => {
    if (!authUser) return null;
    return {
      first_name: authUser.first_name ?? '',
      last_name: authUser.last_name ?? '',
      email: authUser.email ?? '',
      profile_image_url: (authUser as { profile_image_url?: string })
        .profile_image_url
    };
  }, [authUser]);

  const displayUserData =
    currentUserData || userData || authUserForDisplay || undefined;

  // --- Event Handlers ---

  const handleCloseNotificationMenu = useCallback(() => {
    setOpenNotificationMenu(null);
  }, []);

  const handleOpenNotificationMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setOpenNotificationMenu(event.currentTarget);
    },
    []
  );

  // --- Render Helpers ---

  const renderUserProfile = () => {
    if (!mounted) return null;

    if (!displayUserData && isLoggedIn) {
      return <UserProfileSkeleton />;
    }
    if (!displayUserData || !isLoggedIn) return null;

    return (
      <Stack direction="row" ml={{ xs: 1, sm: 2 }} alignItems="center">
        {/* Chat Icon */}
        <Link href="/chat" style={{ textDecoration: 'none' }} passHref>
          <Box
            sx={{
              ...classes.iconBox,
              mr: { xs: 0.5, sm: 1 },
              backgroundColor:
                pathname === '/chat' ? COLORS.bgBlue : 'transparent'
            }}
          >
            <IconButton
              aria-label="chat notifications"
              sx={{ borderRadius: 2, p: { xs: '6px', lg: '12px' } }}
            >
              <Badge badgeContent={unreadConversationsCount} color="error">
                <Image
                  height={24}
                  width={24}
                  alt="message"
                  src="/assets/svg/message.svg"
                />
              </Badge>
            </IconButton>
          </Box>
        </Link>

        {/* Notification Icon */}
        <Box sx={classes.iconBox}>
          <IconButton
            aria-label="notifications"
            onClick={handleOpenNotificationMenu}
            sx={{ borderRadius: 2, p: { xs: '6px', lg: '12px' } }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Image
                height={24}
                width={24}
                alt="notification"
                src="/assets/svg/bell-outlined.svg"
              />
            </Badge>
          </IconButton>
          <Menu
            sx={(theme) => ({
              mt: { xs: 0, lg: '40px' },
              '& .MuiPaper-root': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                m: 0,
                '& .MuiList-root': { p: 0 },
                [theme.breakpoints.down('lg')]: {
                  position: 'fixed !important',
                  top: '0 !important',
                  left: '0 !important',
                  width: '100vw !important',
                  height: '100vh !important',
                  maxWidth: '100vw !important',
                  maxHeight: '100vh !important',
                  margin: '0 !important',
                  borderRadius: '0 !important'
                }
              }
            })}
            id="notifications-menu"
            anchorEl={openNotificationMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(openNotificationMenu)}
            onClose={handleCloseNotificationMenu}
            disableScrollLock
          >
            <NotificationPanel onClose={handleCloseNotificationMenu} />
          </Menu>
        </Box>

        {/* Profile Avatar */}
        <Link
          href="/profile?tab=profileSummary"
          style={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            spacing={1.5}
          >
            <Avatar
              src={displayUserData?.profile_image_url}
              sx={{
                width: { lg: 40, xl: 48 },
                height: { lg: 40, xl: 48 },
                backgroundColor: '#E8ECF4',
                display: { xs: 'none', lg: 'flex' }
              }}
            >
              {!displayUserData?.profile_image_url && (
                <Image
                  src={userContained}
                  alt="avatar"
                  width={32}
                  height={32}
                />
              )}
            </Avatar>
            {/* Name/email only from xl — frees space so links don't collide */}
            <Stack sx={{ display: { xs: 'none', xl: 'flex' }, minWidth: 0 }}>
              <Typography
                color="primary"
                variant="body1"
                fontWeight={500}
                noWrap
              >
                {displayUserData.full_name}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap>
                {displayUserData.email}
              </Typography>
            </Stack>
          </Stack>
        </Link>
      </Stack>
    );
  };

  if (!mounted) return null;

  if (isLoggedIn) {
    return displayUserData ? renderUserProfile() : <UserProfileSkeleton />;
  }

  return (
    <Stack
      direction="row"
      gap={{ xs: '8px', lg: '16px', xl: '20px' }}
      alignItems="center"
    >
      <Link href="/login" style={{ textDecoration: 'none' }} passHref>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button variant="contained" sx={classes.loginBtn}>
            Login
          </Button>
        </motion.div>
      </Link>
      <Link href="/sign-up" style={{ textDecoration: 'none' }} passHref>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button variant="outlined" sx={classes.signupBtn}>
            Sign Up
          </Button>
        </motion.div>
      </Link>
    </Stack>
  );
};

export default NavUserActions;
